import {
  Authenticator,
  Image,
  Text,
  View,
  CheckboxField,
  AuthenticatorProps,
  useAuthenticator,
} from "@aws-amplify/ui-react";
import { PropsWithChildren, useEffect } from "react";

import { getCurrentUser } from "@aws-amplify/auth";
import { gql, useMutation } from "@apollo/client";
import PageLoader from "../PageLoader";
import StaticHeader from "../../Product/Layout/Header/StaticHeader";
import { PRIVACY_POLICY, TERMS_OF_SERVICE } from "../../../routes/wixRoutes";
import { useMeta } from "../../../contexts/MetaContext";

import styles from "./claimable-authenticator.module.scss";

type LoginMechanisms = Parameters<typeof Authenticator>[0]["loginMechanisms"];

const AuthenticatorHeader = () => (
  <View className={styles.authHeader}>
    <Image alt="Claimable" src="/images/logo/logo_black_large.svg" />
  </View>
);

const Footer = () => (
  <Text variation="tertiary">© 2025 All rights reserved.</Text>
);

const SignUpAgreeToTermsAndPrivacyPolicy = () => (
  <>
    <Authenticator.SignUp.FormFields />
    <CheckboxField
      name="custom:agreeToTerms"
      value="agree"
      isRequired
      label={
        <>
          I agree to the{" "}
          <a
            href={TERMS_OF_SERVICE}
            target="_blank"
            rel="noopener noreferrer"
            style={{ textDecoration: "underline" }}
          >
            Terms of Service
          </a>{" "}
          and{" "}
          <a
            href={PRIVACY_POLICY}
            target="_blank"
            rel="noopener noreferrer"
            style={{ textDecoration: "underline" }}
          >
            Privacy Policy
          </a>
        </>
      }
    />
  </>
);

const ForcePasswordAgreeToTermsAndPrivacyPolicy = () => (
  <>
    <Authenticator.ForceNewPassword.FormFields />
    <CheckboxField
      name="custom:agreeToTerms"
      value="agree"
      isRequired
      label={
        <>
          I agree to the{" "}
          <a
            href={TERMS_OF_SERVICE}
            target="_blank"
            rel="noopener noreferrer"
            style={{ textDecoration: "underline" }}
          >
            Terms of Service
          </a>{" "}
          and{" "}
          <a
            href={PRIVACY_POLICY}
            target="_blank"
            rel="noopener noreferrer"
            style={{ textDecoration: "underline" }}
          >
            Privacy Policy
          </a>
        </>
      }
    />
  </>
);

const SignInHeader = () => <h1 className={styles.signInHeader}>Sign in</h1>;

const SignUpHeader = () => (
  <h1 className={styles.signInHeader}>Create account</h1>
);

const LoginError = ({ errorMessage }: { errorMessage: string }) => (
  <div className="page_container">
    <div className={styles.claimableAuthenticator}>
      <StaticHeader />
      <div className="max-w-xl mx-auto">
        <p className={styles.loginErrorMessage}>
          {errorMessage}
          <a className="mailto" href="mailto://support@getclaimable.com">
            support@getclaimable.com
          </a>
          {`.`}
        </p>
      </div>
    </div>
  </div>
);

export const SETUP_NEW_USER_WITH_EXISTING_REFERRALS = gql(`
  mutation SetupNewUserWithExistingReferrals($userId: ID!) {
    setupNewUserWithExistingReferrals(userId: $userId) {
      appealId
    }
  }
`);

type ClaimableAuthenticatorProps = {
  hideSignUp?: boolean;
  defaultTab?: "signUp" | "signIn";
  formFields?: AuthenticatorProps["formFields"];
  onLoginComplete?: () => void;
};

const ClaimableAuthenticator = ({
  children,
  hideSignUp = false,
  defaultTab = "signUp",
  formFields,
  onLoginComplete,
}: PropsWithChildren<ClaimableAuthenticatorProps>) => {
  const loginMechanisms: LoginMechanisms = ["email"];
  const themeComponents = {
    Header: AuthenticatorHeader,
    Footer,
    SignUp: {
      FormFields: SignUpAgreeToTermsAndPrivacyPolicy,
      Header: SignUpHeader,
    },
    ForceNewPassword: {
      FormFields: ForcePasswordAgreeToTermsAndPrivacyPolicy,
    },
    SignIn: {
      Header: SignInHeader,
    },
  };

  const { authStatus } = useAuthenticator();
  const { hasUserAuthenticated, setHasUserAuthenticated } = useMeta();
  const [
    attachNewUserToExistingAppeals,
    { loading: mutationRunning, error: mutationError },
  ] = useMutation(SETUP_NEW_USER_WITH_EXISTING_REFERRALS);

  useEffect(() => {
    async function asyncMethod() {
      if (authStatus === "authenticated" && !hasUserAuthenticated) {
        const user = await getCurrentUser();
        await attachNewUserToExistingAppeals({
          variables: {
            userId: user.userId,
          },
        });
        setHasUserAuthenticated(true);

        if (onLoginComplete) {
          onLoginComplete();
        }
      }
    }

    asyncMethod();
  }, [authStatus]);

  if (mutationError) {
    return (
      <LoginError
        errorMessage={`Failed to complete setting up your account. We're looking into it. If you don't hear from us, please use the link below to contact us with details: `}
      />
    );
  }

  if (mutationRunning) {
    return (
      <div className={styles.claimableAuthenticator}>
        <StaticHeader />
        <PageLoader />
      </div>
    );
  }

  return (
    <div className={styles.claimableAuthenticator}>
      <Authenticator
        loginMechanisms={loginMechanisms}
        components={themeComponents}
        hideSignUp={hideSignUp}
        initialState={defaultTab}
        formFields={formFields}
      >
        {children}
      </Authenticator>
    </div>
  );
};

export default ClaimableAuthenticator;
